// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module MixpanelProvider
 * @package @geenius/analytics
 * @description Implements the shared Mixpanel provider using the optional
 * `mixpanel-browser` client. The provider lazily initializes Mixpanel so
 * consumers only load the dependency when configured.
 */

import type { AnalyticsEvent, PageView } from "../types";
import {
  type AnalyticsProviderConfig,
  BaseAnalyticsProvider,
  type UserTraits,
} from "./interface";

let importOptionalPeer = async <T>(
  specifier: string,
): Promise<{ default: T }> =>
  (await import(/* @vite-ignore */ specifier)) as { default: T };

/**
 * Mixpanel analytics provider backed by the optional browser tracker client.
 */
export class MixpanelProvider extends BaseAnalyticsProvider {
  readonly name = "mixpanel" as const;
  private mixpanel: MixpanelClient | null = null;

  /**
   * Stores the Mixpanel configuration for later lazy client initialization.
   *
   * @param config Provider configuration containing the Mixpanel project token.
   * @returns Nothing.
   */
  init(config: AnalyticsProviderConfig): void {
    super.init(config);
  }

  private async getClient(): Promise<MixpanelClient> {
    if (!this.mixpanel) {
      const config = this.getRequiredConfig();
      const mixpanelModule =
        await importOptionalPeer<MixpanelClient>("mixpanel-browser");
      this.mixpanel = mixpanelModule.default;
      this.mixpanel.init(config.apiKey, {
        api_host: config.host || "https://api-js.mixpanel.com",
        // Page views are emitted manually through the shared tracker so Mixpanel
        // does not double count route changes.
        track_pageview: false,
        debug: config.debug ?? false,
      });
    }
    return this.mixpanel;
  }

  /**
   * Sends a shared analytics event to Mixpanel.
   *
   * @param event Shared analytics event payload.
   * @returns Nothing.
   */
  async trackEvent(event: AnalyticsEvent): Promise<void> {
    const client = await this.getClient();
    client.track(event.name, {
      ...event.properties,
      $current_url: event.url,
      $referrer: event.referrer,
      event_type: event.type,
      device_type: event.device?.device,
      browser: event.device?.browser,
      os: event.device?.os,
    });
  }

  /**
   * Sends a page-view payload to Mixpanel.
   *
   * @param pageView Shared page-view payload.
   * @returns Nothing.
   */
  async trackPageView(pageView: PageView): Promise<void> {
    const client = await this.getClient();
    client.track("Page View", {
      ...pageView.properties,
      url: pageView.url,
      title: pageView.title,
      referrer: pageView.referrer,
      duration: pageView.duration,
    });
  }

  /**
   * Identifies the current user and syncs optional traits to Mixpanel People.
   *
   * @param userId Stable application user identifier.
   * @param traits Optional user traits forwarded to Mixpanel.
   * @returns Nothing.
   */
  async identify(userId: string, traits?: UserTraits): Promise<void> {
    const client = await this.getClient();
    client.identify(userId);
    if (traits) {
      client.people.set(traits);
    }
  }

  /**
   * Updates Mixpanel People traits for an already identified user.
   *
   * @param userId Stable application user identifier.
   * @param traits User traits forwarded to Mixpanel People.
   * @returns Nothing.
   */
  async setUserProperties(userId: string, traits: UserTraits): Promise<void> {
    const client = await this.getClient();
    client.identify(userId);
    client.people.set(traits);
  }

  /**
   * Resets the Mixpanel client identity and local state.
   *
   * @returns Nothing.
   */
  async reset(): Promise<void> {
    const client = await this.getClient();
    client.reset();
  }
}
